import React, { useCallback, useEffect } from 'react';
import { SearchBox } from '@fluentui/react/lib/SearchBox';
import styled from 'styled-components/macro';
import { ResourceStrings } from '../../../ResourceStrings';
import { useSelector } from 'react-redux';
import { RootState } from '../../../store/rootState';
import { OperationState } from '../../../store/appState';
import * as Actions from '../../../store/actions';
import _ from 'lodash';
import { ProgressIndicator } from '@fluentui/react/lib/ProgressIndicator';
import { maximumPostTitleLength, minimumPostTitleLength } from '../../../constants/validation';
import { testFeedbackTitlePrefix } from '../../../constants/config';

// Show a message 'X characters left' if less than these many are left
const charsLeftMessageThreshold = 10;

export interface TitleEditorProps {
    id?: string;
    onSearch: (searchFor: string, updateCountsByType: boolean, resultsToSkip: number) => void;
    placeholderText?: string;
    ariaLabelText: string;
}

export default function TitleEditor(props: TitleEditorProps): JSX.Element {
    const state = useSelector((state: RootState) => state.appState);
    const reportTitle = useSelector((state: RootState) => state.appState.reportTitle);

    const search = useCallback((searchFor: string) => {
        Actions.rapSetTitle(searchFor);
        Actions.searchUpdate(searchFor);
    }, [state]);

    useEffect(() => {
        const onSearchRequestedDebounced = _.debounce(
            (searchFor: string, updateCountsByType: boolean, resultsToSkip: number) => {
                if (searchFor) {
                    props.onSearch(searchFor, updateCountsByType, resultsToSkip);
                }
            }
            , 200);
        onSearchRequestedDebounced(reportTitle, true, 0);
    }, [reportTitle, props.onSearch]);

    const titlePlaceholder = props.placeholderText ?? ResourceStrings.postTitlePlaceholder();

    const ignoreValidations = reportTitle.startsWith(testFeedbackTitlePrefix);
    const isInvalid = !ignoreValidations && reportTitle && reportTitle.length < minimumPostTitleLength;

    return <>
        <Search
            data-cy="rap-post-title-search"
            className={isInvalid ? "isInvalid" : ""}
            value={reportTitle}
            aria-describedby="title-messages"
            ariaLabel={props.ariaLabelText}
            autoFocus
            onChange={(e) => search(e?.target?.value ?? "")}
            placeholder={titlePlaceholder}
            iconProps={{ iconName: 'SingleColumnEdit' }}
            maxLength={maximumPostTitleLength}
        />

        {state.problems.state === OperationState.Executing && <ProgressIndicator
            barHeight={2}
            styles={{ itemProgress: { padding: "0" } }}
        />}

        <div id="title-messages">
            {isInvalid && (
                <Error>{ResourceStrings.errorTextRangeBetween(minimumPostTitleLength, maximumPostTitleLength)}</Error>
            )}

            {reportTitle && (maximumPostTitleLength - reportTitle.length < charsLeftMessageThreshold) && (
                <Info>{ResourceStrings.charactersLeft(maximumPostTitleLength - reportTitle.length)}</Info>
            )}
        </div>
    </>;
}

const Search = styled(SearchBox)`
    height: 3.2rem;
    /* For high contrast mode */
    border: solid 1px var(--color-surface-dark2);


    &.isInvalid {
        border: solid 1px var(--color-error);
    }

    .ms-SearchBox-field {
        font-weight: normal;
    }

    .ms-SearchBox-field::placeholder {
        font-weight: 350;
        font-size: 1.8rem;
    }

    .ms-SearchBox-icon {
        color: var(--color-on-surface);
    }

    input {
        padding: 0 .6rem;
    }
`;

const Error = styled.div`
    color: var(--color-error);
    padding: 0.5rem 1rem .5rem 1.2rem;
`;

const Info = styled(Error)`
    color: var(--color-on-surface-light);
`;
